/**
 * RoomTypes — native staging room-type selector (lifted from
 * `common/StagingFlow/RoomStep/RoomTypes.component.tsx`, rewired onto the module
 * working context + `patchRoom` + plain-JS `roomTypes` array). Analytics dropped
 * (DF-staging-analytics).
 */

import { FC, useCallback, useMemo } from 'react'

import { GRAY_900, YELLOW_600 } from 'constants/styling/theme'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import Stack from '@mui/material/Stack'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import Typography from '@mui/material/Typography'
import { VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'

import styles from 'components/common/StagingFlow/RoomStep/RoomStyle.module.sass'

import { useStagingWorkingState } from '../StagingWorkingState.context'
import { useStagingStylesFilter } from '../useStagingStylesFilter'
import { setAdd, setDelete } from '../stagingRoom.logic'

export const RoomTypes: FC = () => {
  const { availableRoomTypes } = useStagingStylesFilter()
  const { activeRoom, patchRoom } = useStagingWorkingState()

  const { t } = useTranslation(['style_selection_order_step', 'virtual_furniture_room_type'])

  const roomTypeToggleValues: ToggleFilterValue<VirtualFurnitureRoomType>[] = useMemo(
    () =>
      availableRoomTypes.map((roomType) => {
        const isEmptyTypeSelected = !!activeRoom?.roomTypes.includes(VirtualFurnitureRoomType.EMPTY_ROOM)
        const areNonEmptyTypesSelected = (activeRoom?.roomTypes.length ?? 0) > 0 && !isEmptyTypeSelected

        let isDisabled = false
        if (isEmptyTypeSelected && roomType !== VirtualFurnitureRoomType.EMPTY_ROOM) isDisabled = true
        if (areNonEmptyTypesSelected && roomType === VirtualFurnitureRoomType.EMPTY_ROOM) isDisabled = true

        return {
          value: roomType,
          displayName: t(`virtual_furniture_room_type:${roomType}`),
          disabled: isDisabled,
        }
      }),
    [activeRoom?.roomTypes, availableRoomTypes, t]
  )

  const onSelectRoomType = useCallback(
    (type: VirtualFurnitureRoomType) => {
      if (!activeRoom) return
      patchRoom(activeRoom.key, { roomTypes: setAdd(activeRoom.roomTypes, type) })
    },
    [activeRoom, patchRoom]
  )

  const onUnselectRoomType = useCallback(
    (type: VirtualFurnitureRoomType) => {
      if (!activeRoom) return
      patchRoom(activeRoom.key, { roomTypes: setDelete(activeRoom.roomTypes, type) })
    },
    [activeRoom, patchRoom]
  )

  if (!activeRoom) return null

  return (
    <div>

      <Stack gap={0.4} marginBottom={1.6} textAlign="center">

        <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
          {t('style_selection_order_step:room_types')}
          &nbsp;
          <span style={{ fontSize: '1.4rem', color: YELLOW_600, fontWeight: 'bold' }}>
            ({t('style_selection_order_step:mandatory')})
          </span>
        </Typography>

        <Typography variant="text-sm">
          {t('style_selection_order_step:room_types_description')}
        </Typography>

      </Stack>

      <SelectableButtonsGrid
        values={roomTypeToggleValues}
        selectedValue={activeRoom.roomTypes}
        className={styles.roomGridContainer}
        onSelect={onSelectRoomType}
        onUnselect={onUnselectRoomType}
        showCheckbox
        activeButtonClickable
      />

    </div>
  )
}
